/**
 * useTaskDetail — the React binding for a SINGLE task's page (`/tasks/:id`).
 *
 * Strict layering: the page calls THIS hook; the hook calls the tasks store +
 * the sprint-board estimate store + the goal/workstream/service/sprint stores to
 * hydrate the task, its saved estimate (full breakdown), and the option lists
 * for the OPTIONAL goal/workstream/service link selectors. The page never
 * imports a store or the api directly.
 *
 * This follows the same shape as {@link useGoal}/{@link useWorkstream}: load one
 * document by id + the small summary lists needed to render/resolve links, and
 * expose focused save operations. Estimate CREATION/EDIT is delegated to
 * {@link useTaskEstimate} so the same persistence path serves the task page and
 * the post-create "Estimate now" step; after an estimate is saved the page calls
 * {@link refresh}.
 */

import { useCallback, useEffect, useMemo, useState } from 'react';
import { useApi } from '../../hooks/useApi';
import { TaskStore } from '../tasks/taskStore';
import { buildResearchQuestion } from '../tasks/kbResearch';
import {
  blankTaskDraft,
  draftFromTask,
  nextSubtaskBoardStatus,
  subtaskProgress as computeSubtaskProgress,
  type SubtaskProgress,
} from '../tasks/taskModel';
import type { BoardStatus, Task, TaskDraft } from '../tasks/types';
import { GoalStore } from '../goals/goalStore';
import { WorkstreamStore } from '../workstreams/workstreamStore';
import { ServiceStore } from '../services/serviceStore';
import { SprintStore } from './stores/sprintStore';
import { TaskEstimateStore } from './estimate/taskEstimateStore';
import { TaskUpdateStore } from './stores/taskUpdateStore';
import { TeamDocumentStore } from '../documents/documentStore';
import { blankDocumentDraft } from '../documents/documentModel';
import { sortUpdatesByRecency } from './models/sprintboardModel';
import type { GoalSummary } from '../goals/types';
import type { WorkstreamSummary } from '../workstreams/types';
import type { ServiceSummary } from '../services/types';
import type { Sprint, TaskUpdate, TaskUpdateDraft } from './models/types';
import type { TaskEstimate } from './estimate/types';
import type { Blocker } from '../tasks/types';
import type { TeamDocument, TeamDocumentSummary } from '../documents/types';

/** Partial link update the task page applies from the optional selectors. */
export interface TaskLinks {
  goalId?: string;
  workstreamId?: string;
  serviceId?: string;
}

/**
 * Compact fields the task page's "+ Add subtask" quick-create supplies. Title is
 * required; owner is optional. An OPTIONAL compact estimate (effort days + story
 * points + confidence) can be captured inline while creating the subtask — all
 * optional, so a subtask stays creatable with a title alone. Everything else
 * (type, room, sprint, lane) is inherited from the parent.
 */
export interface AddSubtaskInput {
  title: string;
  owner?: string;
  /** Optional engineering effort in ideal days. */
  effortDays?: number;
  /** Optional story points. */
  storyPoints?: number;
  /** Optional confidence percentage (0–100). */
  confidence?: number;
}

/**
 * Compact fields the task page's "+ Add Document" form supplies. The document is
 * written to the SHARED team-documents collection tagged with the task (and its
 * goal) so it also surfaces in the global Document Bank — no duplicate entry.
 */
export interface AddTaskDocumentInput {
  title: string;
  docType: string;
  documentLink: string;
  /** Short context — used as both the discovery summary and the "why". */
  context: string;
}

export interface UseTaskDetailResult {
  /** The hydrated task, or null when not loaded / not found. */
  task: Task | null;
  /** The task's saved estimate (full inputs + breakdown), or null. */
  estimate: TaskEstimate | null;
  /** The task's sprint, or null when it sits in the backlog / unresolved. */
  sprint: Sprint | null;
  /** Goal summaries (to populate + resolve the optional goal link). */
  goals: GoalSummary[];
  /** Workstream summaries (to populate + resolve the optional workstream link). */
  workstreams: WorkstreamSummary[];
  /** Service summaries (to populate + resolve the optional service link). */
  services: ServiceSummary[];
  /** The task's standup updates (newest-first). Power the Daily Scrum too. */
  updates: TaskUpdate[];
  /** Team documents tagged with this task (from the shared Document Bank). */
  documents: TeamDocumentSummary[];
  /** This task's non-deleted subtasks (creation order from the store). */
  subtasks: Task[];
  /** Done/total roll-up over {@link subtasks}. */
  subtaskProgress: SubtaskProgress;
  loading: boolean;
  error: string | null;
  refresh: () => Promise<void>;
  /** True while an AI Knowledge Base Research generation is in flight. */
  researching: boolean;
  /** Inline error from the last KB research attempt, or null. */
  researchError: string | null;
  /**
   * Generates an AI 'Knowledge Base Research' answer for the loaded task: builds
   * the enriched question (resolving the linked goal/workstream/service NAMES),
   * grounds it against the team KB via RetrieveAndGenerate, and persists the
   * markdown answer + citations on the task. KB/model failures surface via
   * {@link researchError} and leave the task otherwise unaffected.
   */
  generateKbResearch: () => Promise<void>;
  /** Applies an edited draft to the task. */
  saveTask: (draft: TaskDraft) => Promise<Task>;
  /**
   * Assigns (or, with an empty string, clears) the task's OWNER inline — the
   * sprint-view "assign later" path. Updates the loaded task IN PLACE (no
   * full-page reload/flash) and reconciles with the persisted task.
   */
  assignOwner: (owner: string) => Promise<Task>;
  /** Applies only the optional goal/workstream/service links. */
  saveLinks: (links: TaskLinks) => Promise<Task>;
  /** Posts a task update (Completed / Next / Blocker) — powers the Daily Scrum. */
  addUpdate: (draft: TaskUpdateDraft) => Promise<TaskUpdate>;
  /** Marks the task blocked with a structured blocker. */
  setBlocked: (blocker: Blocker) => Promise<Task>;
  /** Clears the task's blocked state. */
  unblock: () => Promise<Task>;
  /**
   * Registers a document in the SHARED Document Bank tagged with this task (and
   * its goal). Returns the created record. Appears both here and in the global
   * Documents view — one shared entry, no duplicate.
   */
  addDocument: (input: AddTaskDocumentInput) => Promise<TeamDocument>;
  /**
   * Quick-creates a subtask under this task: type 'Task', roomId + sprintId
   * inherited from the parent, boardStatus 'TODO', with an OPTIONAL inline
   * estimate (effort/SP/confidence). Updates the local subtasks list IN PLACE
   * (optimistic append) — the task page does NOT reload or lose scroll.
   */
  addSubtask: (input: AddSubtaskInput) => Promise<Task>;
  /**
   * Cycles a subtask's board lane: TODO → IN_PROGRESS → DONE → TODO. Patches the
   * subtask in the local list IN PLACE (no full-page refresh).
   */
  cycleSubtask: (subtask: Task) => Promise<Task>;
  /**
   * Sets (or clears with `undefined`) a subtask's custom effort in ideal days.
   * Patches the subtask in the local list IN PLACE so the parent's Total Effort
   * updates without a full-page refresh.
   */
  updateSubtaskEffort: (subtask: Task, effortDays: number | undefined) => Promise<Task>;
  /**
   * Soft-deletes a subtask (same pattern as tasks) and removes it from the local
   * list IN PLACE (no full-page refresh).
   */
  removeSubtask: (id: string) => Promise<void>;
}

export function useTaskDetail(id: string | undefined): UseTaskDetailResult {
  const api = useApi();
  const stores = useMemo(
    () => ({
      tasks: new TaskStore(api),
      estimates: new TaskEstimateStore(api),
      goals: new GoalStore(api),
      workstreams: new WorkstreamStore(api),
      services: new ServiceStore(api),
      sprints: new SprintStore(api),
      updates: new TaskUpdateStore(api),
      documents: new TeamDocumentStore(api),
    }),
    [api],
  );

  const [task, setTask] = useState<Task | null>(null);
  const [estimate, setEstimate] = useState<TaskEstimate | null>(null);
  const [sprint, setSprint] = useState<Sprint | null>(null);
  const [goals, setGoals] = useState<GoalSummary[]>([]);
  const [workstreams, setWorkstreams] = useState<WorkstreamSummary[]>([]);
  const [services, setServices] = useState<ServiceSummary[]>([]);
  const [updates, setUpdates] = useState<TaskUpdate[]>([]);
  const [documents, setDocuments] = useState<TeamDocumentSummary[]>([]);
  const [subtasks, setSubtasks] = useState<Task[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [researching, setResearching] = useState(false);
  const [researchError, setResearchError] = useState<string | null>(null);

  const refresh = useCallback(async () => {
    if (!id) {
      setTask(null);
      setEstimate(null);
      setError(null);
      setLoading(false);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const [loaded, goalRows, wsRows, svcRows] = await Promise.all([
        stores.tasks.get(id),
        stores.goals.listSummaries(),
        stores.workstreams.listSummaries(),
        stores.services.listSummaries(),
      ]);
      setGoals(goalRows);
      setWorkstreams(wsRows);
      setServices(svcRows);

      if (!loaded || loaded.deleted) {
        setTask(null);
        setEstimate(null);
        setSprint(null);
        setUpdates([]);
        setDocuments([]);
        setSubtasks([]);
        setError('Task not found.');
        return;
      }
      setTask(loaded);

      // Hydrate the estimate (full breakdown) + sprint + updates + tagged
      // documents in parallel; all are best-effort — a missing/stale link must
      // not fail the whole page.
      const [est, spr, taskUpdates, allDocs] = await Promise.all([
        loaded.estimateId
          ? stores.estimates.get(loaded.estimateId).catch(() => undefined)
          : Promise.resolve(undefined),
        loaded.sprintId
          ? stores.sprints.get(loaded.sprintId).catch(() => undefined)
          : Promise.resolve(undefined),
        stores.updates.listByTask(loaded.id).catch(() => []),
        stores.documents.listSummaries().catch(() => []),
      ]);
      setEstimate(est && !est.deleted ? est : null);
      setSprint(spr && !spr.deleted ? spr : null);
      setUpdates(sortUpdatesByRecency(taskUpdates));
      setDocuments(
        allDocs.filter((d) => (d.relatedStoryIds ?? []).includes(loaded.id)),
      );
      // A subtask has no subtasks of its own; skip the fetch for one.
      const kids = loaded.parentTaskId
        ? []
        : await stores.tasks.listByParent(loaded.id).catch(() => []);
      setSubtasks(kids);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to load task.');
    } finally {
      setLoading(false);
    }
  }, [id, stores]);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  const saveTask = useCallback(
    async (draft: TaskDraft) => {
      if (!task) throw new Error('No task loaded to save.');
      const saved = await stores.tasks.applyDraft(task, draft);
      await refresh();
      return saved;
    },
    [task, stores, refresh],
  );

  const assignOwner = useCallback(
    async (owner: string) => {
      if (!task) throw new Error('No task loaded to update.');
      const saved = await stores.tasks.assignOwner(task, owner);
      // In-place reconcile — no full-page reload/flash, scroll preserved.
      setTask(saved);
      return saved;
    },
    [task, stores],
  );

  const saveLinks = useCallback(
    async (links: TaskLinks) => {
      if (!task) throw new Error('No task loaded to update.');
      const draft: TaskDraft = {
        ...draftFromTask(task),
        goalId: links.goalId ?? '',
        workstreamId: links.workstreamId ?? '',
        serviceId: links.serviceId ?? '',
      };
      const saved = await stores.tasks.applyDraft(task, draft);
      await refresh();
      return saved;
    },
    [task, stores, refresh],
  );

  const generateKbResearch = useCallback(async () => {
    if (!task) throw new Error('No task loaded to research.');
    setResearching(true);
    setResearchError(null);
    try {
      // Resolve the linked entity NAMES (same lookups the page uses) so the
      // question carries human-readable context, not opaque ids.
      const question = buildResearchQuestion(task, {
        goalName: goals.find((g) => g.id === task.goalId)?.title,
        workstreamName: workstreams.find((w) => w.id === task.workstreamId)?.title,
        serviceName: services.find((s) => s.id === task.serviceId)?.name,
      });
      await stores.tasks.researchAndSave(task, { question });
      await refresh();
    } catch (e) {
      setResearchError(
        e instanceof Error
          ? e.message
          : 'Knowledge Base research is unavailable right now.',
      );
    } finally {
      setResearching(false);
    }
  }, [task, goals, workstreams, services, stores, refresh]);

  const addUpdate = useCallback(
    async (draft: TaskUpdateDraft) => {
      if (!task) throw new Error('No task loaded to update.');
      const saved = await stores.updates.create(task.roomId, {
        ...draft,
        taskId: task.id,
      });
      await refresh();
      return saved;
    },
    [task, stores, refresh],
  );

  const setBlocked = useCallback(
    async (blocker: Blocker) => {
      if (!task) throw new Error('No task loaded to update.');
      const saved = await stores.tasks.setBlocked(task, true, blocker);
      await refresh();
      return saved;
    },
    [task, stores, refresh],
  );

  const unblock = useCallback(async () => {
    if (!task) throw new Error('No task loaded to update.');
    const saved = await stores.tasks.setBlocked(task, false);
    await refresh();
    return saved;
  }, [task, stores, refresh]);

  const addSubtask = useCallback(
    async (input: AddSubtaskInput) => {
      if (!task) throw new Error('No task loaded to update.');
      const draft: TaskDraft = {
        ...blankTaskDraft('TODO'),
        title: input.title.trim(),
        type: 'Task',
        boardStatus: 'TODO',
        owner: input.owner?.trim() || '',
        // Optional inline estimate captured while creating the subtask.
        effortDays: input.effortDays,
        storyPoints: input.storyPoints,
        confidence: input.confidence,
        // Inherit the parent's sprint (or backlog) and link to the parent.
        sprintId: task.sprintId ?? null,
        parentTaskId: task.id,
      };
      const saved = await stores.tasks.create(task.roomId, draft);
      // Optimistic in-place append — the page does NOT reload or lose scroll.
      // `saved` is the persisted task, so the list stays consistent.
      setSubtasks((prev) => [...prev, saved]);
      return saved;
    },
    [task, stores],
  );

  const cycleSubtask = useCallback(
    async (subtask: Task) => {
      const next: BoardStatus = nextSubtaskBoardStatus(subtask.boardStatus);
      const saved = await stores.tasks.moveToBoardStatus(subtask, next);
      // Patch the single subtask in place — no full-page refresh.
      setSubtasks((prev) => prev.map((s) => (s.id === subtask.id ? saved : s)));
      return saved;
    },
    [stores],
  );

  const removeSubtask = useCallback(
    async (subtaskId: string) => {
      await stores.tasks.softDelete(subtaskId);
      // Drop it from the list in place — no full-page refresh.
      setSubtasks((prev) => prev.filter((s) => s.id !== subtaskId));
    },
    [stores],
  );

  const updateSubtaskEffort = useCallback(
    async (subtask: Task, effortDays: number | undefined) => {
      const saved = await stores.tasks.setEffortDays(subtask, effortDays);
      // Patch the single subtask in place so Total Effort recomputes.
      setSubtasks((prev) => prev.map((s) => (s.id === subtask.id ? saved : s)));
      return saved;
    },
    [stores],
  );

  const addDocument = useCallback(
    async (input: AddTaskDocumentInput) => {
      if (!task) throw new Error('No task loaded to update.');
      const context = input.context.trim();
      const draft = blankDocumentDraft({
        title: input.title.trim(),
        docType: input.docType.trim() || 'Design Doc',
        documentLink: input.documentLink.trim(),
        // The bank requires a summary for discovery; the short context serves as
        // both the summary and the "why".
        summary: context || input.title.trim(),
        contextWhy: context,
        // Tag with the task (and its goal) so it appears on the task AND in the
        // global Document Bank as one shared entry.
        relatedStoryIds: [task.id],
        relatedGoalId: task.goalId || undefined,
        relatedServices: task.serviceId ? [task.serviceId] : [],
      });
      const saved = await stores.documents.create(draft);
      await refresh();
      return saved;
    },
    [task, stores, refresh],
  );

  return {
    task,
    estimate,
    sprint,
    goals,
    workstreams,
    services,
    updates,
    documents,
    subtasks,
    subtaskProgress: computeSubtaskProgress(subtasks),
    loading,
    error,
    refresh,
    researching,
    researchError,
    generateKbResearch,
    saveTask,
    assignOwner,
    saveLinks,
    addUpdate,
    setBlocked,
    unblock,
    addDocument,
    addSubtask,
    cycleSubtask,
    updateSubtaskEffort,
    removeSubtask,
  };
}
